import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contractNotesApi, supportingDataApi, commonApi } from '../services/api';
import { ContractNote, Scheme } from '../types/client';
import styles from './ContractNotes.module.css';
import shared from '@/components/utils/shared.module.css';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { FilterSelect, Button, SideTag } from '@/components/ui';
import { formatNumber as formatFixed } from '@/utils/formatters';
import type { FilterOption } from '@/components/ui';

type SelectOption = FilterOption;

type SortField =
  | 'contract_no'
  | 'trade_date'
  | 'sell_buy'
  | 'adj_quantity'
  | 'adj_mkt_rate'
  | 'adj_mkt_amount'
  | 'adj_net_amt'
  | 'created_ts';

const CSV_HEADERS = [
  'Contract No', 'Party Code', 'Custody Code', 'Symbol', 'Company', 'Scheme',
  'Series', 'Settlement No', 'Settlement Type', 'Sell/Buy', 'Trade Date',
  'Quantity', 'Market Rate', 'Market Amount', 'Brokerage', 'STT',
  'Service Tax', 'SEBI Tax', 'Turnover Tax', 'Stamp Duty', 'Net Rate', 'Net Amount',
  'Created At',
];

const escapeCSV = (val: any): string => {
  if (val === undefined || val === null) return '';
  const str = String(val);
  return str.includes(',') || str.includes('"') || str.includes('\n')
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const PAGE_SIZE = 20;

const noteToCSVRow = (note: ContractNote): string[] => [
  note.contract_no,
  note.broker_code,
  note.custody_code ?? '',
  note.company_symbol,
  note.company_name,
  note.scheme_code,
  note.series ?? '',
  note.sett_no != null ? String(note.sett_no) : '',
  note.sett_type ?? '',
  note.trade_type,
  note.trade_date,
  note.adj_quantity != null ? String(note.adj_quantity) : '',
  note.adj_mkt_rate != null ? String(note.adj_mkt_rate) : '',
  note.adj_mkt_amount != null ? String(note.adj_mkt_amount) : '',
  note.brokerage != null ? String(note.brokerage) : '',
  note.stt != null ? String(note.stt) : '',
  note.ser_tax != null ? String(note.ser_tax) : '',
  note.sebi_tax != null ? String(note.sebi_tax) : '',
  note.turn_tax != null ? String(note.turn_tax) : '',
  note.stamp_duty != null ? String(note.stamp_duty) : '',
  note.net_rate != null ? String(note.net_rate) : '',
  note.adj_net_amt != null ? String(note.adj_net_amt) : '',
  note.created_ts ? new Date(note.created_ts).toLocaleString('en-IN') : '',
];

const ContractNotesList: React.FC = () => {
  const [contractNotes, setContractNotes] = useState<ContractNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [csvLoading, setCsvLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<SelectOption | null>(null);
  const [selectedTradeType, setSelectedTradeType] = useState<SelectOption | null>(null);
  const [selectedScheme, setSelectedScheme] = useState<SelectOption | null>(null);
  const [schemeOptions, setSchemeOptions] = useState<SelectOption[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const tradeTypeOptions: SelectOption[] = [
    { value: '1', label: 'Buy' },
    { value: '2', label: 'Sell' },
  ];

  const [sortField, setSortField] = useState<SortField>('trade_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigate();

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    commonApi.getLastWorkingDay()
      .then(data => {
        setStartDate(data.last_working_day || today);
        setEndDate(today);
      })
      .catch(() => {
        setStartDate(today);
        setEndDate(today);
      });
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      if (!startDate && !endDate) { setCompanyOptions([]); return; }
      const params: any = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      params.group_by = 'company';
      const response = await contractNotesApi.getContractNotesSummary(params);
      const options = (response.summary || []).map((item: any) => ({
        value: item.company__id.toString(),
        label: `${item.company__symbol} - ${item.company__name}`
      }));
      setCompanyOptions(options);
    } catch (err) {
      console.error('Error loading companies from contract notes:', err);
      setCompanyOptions([]);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate || endDate) loadCompanies();
  }, [startDate, endDate, loadCompanies]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const schemes = await supportingDataApi.getSchemes();
        const options = schemes.map((scheme: Scheme) => ({
          value: scheme.id.toString(),
          label: `${scheme.code} - ${scheme.display_name}`
        }));
        setSchemeOptions(options);
      } catch (err) {
        console.error('Error loading schemes:', err);
      }
    };
    loadSchemes();
  }, []);

  const buildFilterParams = useCallback(() => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedCompany) params.company = parseInt(selectedCompany.value);
    if (selectedTradeType) params.sell_buy = parseInt(selectedTradeType.value);
    if (selectedScheme) params.party_code__scheme = parseInt(selectedScheme.value);
    if (searchQuery.trim()) params.search = searchQuery.trim();
    return params;
  }, [startDate, endDate, selectedCompany, selectedTradeType, selectedScheme, searchQuery]);

  const loadContractNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        ...buildFilterParams(),
        page: currentPage,
        page_size: PAGE_SIZE,
        ordering: `${sortDir === 'desc' ? '-' : ''}${sortField}`,
      };
      const response = await contractNotesApi.getContractNotes(params);
      setContractNotes(response.results);
      setTotalCount(response.count);
      setTotalPages(Math.ceil(response.count / PAGE_SIZE));
    } catch (err: any) {
      console.error('Error loading contract notes:', err);
      setError(err.response?.data?.message || 'Failed to load contract notes');
    } finally {
      setLoading(false);
    }
  }, [currentPage, buildFilterParams, sortField, sortDir]);

  useEffect(() => {
    if (startDate || endDate) loadContractNotes();
  }, [loadContractNotes, startDate, endDate]);

  const handleDownloadCSV = async () => {
    setCsvLoading(true);
    try {
      const params: any = {
        ...buildFilterParams(),
        ordering: `${sortDir === 'desc' ? '-' : ''}${sortField}`,
      };
      const response = await contractNotesApi.exportAllContractNotes(params);
      const rows = response.results.map(noteToCSVRow);
      const csvContent = [CSV_HEADERS, ...rows]
        .map(row => row.map(escapeCSV).join(','))
        .join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract-notes${startDate ? `_${startDate}` : ''}${endDate ? `_to_${endDate}` : ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading CSV:', err);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as SortField);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const formatNumber = (value: number | undefined, decimals = 2): string => {
    if (value === undefined || value === null) return '-';
    return formatFixed(value, decimals);
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

  const handleDateChange = (type: 'start' | 'end', value: string) => {
    if (type === 'start') setStartDate(value);
    else setEndDate(value);
    setCurrentPage(1);
  };

  const hasActiveFilters = !!(selectedCompany || selectedTradeType || selectedScheme || searchQuery.trim());

  const clearFilters = () => {
    setSelectedCompany(null);
    setSelectedTradeType(null);
    setSelectedScheme(null);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const activeFilterCount = [selectedCompany, selectedTradeType, selectedScheme].filter(Boolean).length;
  const columns: ColumnDef<ContractNote>[] = [
    { key: 'contract_no', header: 'Contract No', sortable: true,
      cellClassName: () => shared['cell-strong'], render: r => r.contract_no },
    { key: 'broker_code', header: 'Party Code', render: r => r.broker_code },
    { key: 'custody_code', header: 'Custody Code', render: r => r.custody_code ?? '-' },
    { key: 'company_symbol', header: 'Symbol', render: r => r.company_symbol },
    { key: 'company_name', header: 'Company',
      cellClassName: () => shared['col-mid'], render: r => r.company_name },
    { key: 'scheme_code', header: 'Scheme', render: r => r.scheme_code },
    { key: 'series', header: 'Series', render: r => r.series ?? '-' },
    { key: 'sett_no', header: 'Sett. No', render: r => r.sett_no ?? '-' },
    { key: 'sett_type', header: 'Sett. Type', render: r => r.sett_type ?? '-' },
    { key: 'sell_buy', header: 'Sell/Buy', sortable: true,
      render: r => (
        <SideTag side={r.sell_buy === 1 ? 'buy' : 'sell'}>{r.trade_type}</SideTag>
      ) },
    { key: 'trade_date', header: 'Trade Date', sortable: true, render: r => formatDate(r.trade_date) },
    { key: 'adj_quantity', header: 'Quantity', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_quantity, 0) },
    { key: 'adj_mkt_rate', header: 'Market Rate (₹)', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_mkt_rate, 4) },
    { key: 'adj_mkt_amount', header: 'Market Amount (₹)', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_mkt_amount, 2) },
    { key: 'brokerage', header: 'Brokerage (₹)', align: 'right', render: r => formatNumber(r.brokerage, 2) },
    { key: 'stt', header: 'STT (₹)', align: 'right', render: r => formatNumber(r.stt, 2) },
    { key: 'ser_tax', header: 'Service Tax (₹)', align: 'right', render: r => formatNumber(r.ser_tax, 2) },
    { key: 'sebi_tax', header: 'SEBI Tax (₹)', align: 'right', render: r => formatNumber(r.sebi_tax, 2) },
    { key: 'turn_tax', header: 'Turnover Tax (₹)', align: 'right', render: r => formatNumber(r.turn_tax, 2) },
    { key: 'stamp_duty', header: 'Stamp Duty (₹)', align: 'right', render: r => formatNumber(r.stamp_duty, 2) },
    { key: 'net_rate', header: 'Net Rate', align: 'right', render: r => formatNumber(r.net_rate, 4) },
    { key: 'adj_net_amt', header: 'Net Amount (₹)', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_net_amt, 2) },
    { key: 'created_ts', header: 'Created At', sortable: true,
      render: r => (r.created_ts ? new Date(r.created_ts).toLocaleString('en-IN') : '-') },
    { key: 'actions', header: 'Actions', width: '90px',
      render: r => (
        <Button size="sm" onClick={() => navigate(`/contract-notes/${r.id}`)}>
          View
        </Button>
      ) },
  ];

  return (
    <DataTable<ContractNote>
      title="Contract Notes"
      subtitle="Broker contract notes received for the selected period."
      headerActions={
        <Button variant="primary" onClick={() => navigate('/contract-notes-process')}>
          Upload Contract Notes
        </Button>
      }
      data={contractNotes}
      columns={columns}
      rowKey={row => row.id}
      totalCount={totalCount}
      loading={loading}
      loadingMessage="Loading contract notes…"
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={PAGE_SIZE}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      searchQuery={searchQuery}
      onSearchChange={value => { setSearchQuery(value); setCurrentPage(1); }}
      searchPlaceholder="Search by contract no, party code, symbol, company…"
      hasActiveFilters={hasActiveFilters}
      activeFilterCount={activeFilterCount}
      onClearFilters={clearFilters}
      onDownloadCSV={handleDownloadCSV}
      isDownloading={csvLoading}
      emptyMessage="No contract notes found for the selected date range."
      filterSlot={
        <>
          <div className={styles['filter-group']}>
            <label htmlFor="start-date">Start Date</label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange('start', e.target.value)}
              className={styles.input}
            />
          </div>

          <div className={styles['filter-group']}>
            <label htmlFor="end-date">End Date</label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange('end', e.target.value)}
              className={styles.input}
            />
          </div>

          <FilterSelect
            label="Company"
            value={selectedCompany}
            onChange={opt => { setSelectedCompany(opt); setCurrentPage(1); }}
            options={companyOptions}
            placeholder="All Companies"
          />

          <FilterSelect
            label="Trade Type"
            value={selectedTradeType}
            onChange={opt => { setSelectedTradeType(opt); setCurrentPage(1); }}
            options={tradeTypeOptions}
            placeholder="All Types"
          />

          <FilterSelect
            label="Scheme"
            value={selectedScheme}
            onChange={opt => { setSelectedScheme(opt); setCurrentPage(1); }}
            options={schemeOptions}
            placeholder="All Schemes"
          />
        </>
      }
    />
  );
};

export default ContractNotesList;
